import { Tags, Trash2, UserPlus, Users } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useCallback, useMemo } from "react";
import {
  type NativeSyntheticEvent,
  Pressable,
  type TextInputSubmitEditingEventData,
} from "react-native";
import { XStack, YStack } from "tamagui";
import { TagsModal } from "../../../core/tags/TagsModal";
import { BodyTextarea } from "../../../ui/Body";
import { Checkbox } from "../../../ui/Checkbox";
import { Group } from "../../../ui/Group";
import { InfoRow, type InfoRowItemProps } from "../../../ui/InfoRow";
import { PopoverConfirm } from "../../../ui/PopoverConfirm";
import { Sheet } from "../../../ui/Sheet";
import { Title } from "../../../ui/Title";
import { UserUI } from "../../../users/UserUI";
import type CheckboxModel from "./CheckboxModel";

export const CheckboxUIInput: React.FC<{ data: CheckboxModel }> = observer(
  ({ data }) => {
    const saveText = useCallback(
      (e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => {
        data.setLabel(e.nativeEvent.text);
      },
      [data],
    );

    const isChecked = data.data.checked;

    return (
      <BodyTextarea
        placeholder="Add info..."
        submitOnEnter
        blurOnSubmit
        flex={1}
        muted={isChecked}
        strikeThrough={isChecked}
        onSubmitEditing={saveText}
        defaultValue={data.data.label}
      />
    );
  },
);

export const CheckboxUI = observer(({ data }: { data: CheckboxModel }) => {
  const [showUserSheet, setShowUserSheet] = React.useState(false);
  const [showTagsModal, setShowTagsModal] = React.useState(false);

  const infoRowItems = useMemo<InfoRowItemProps[] | undefined>(() => {
    const infos = [];

    if (data.assignees) {
      infos.push({
        icon: Users,
        text: data.assignees,
      });
    }

    if (data.planTags.length > 0 && data.useTags) {
      infos.push({
        icon: Tags,
        text: data.planTags.map((t) => t.label).join(", "),
      });
    }

    if (!infos.length) {
      return;
    }

    return infos;
  }, [data.assignees, data.planTags, data.useTags]);

  return (
    <>
      <XStack
        testID={"check-box-item"}
        flex={1}
        width="100%"
        gap={"$space.small"}
        paddingLeft={15 * data.data.indented}
        justifyContent="space-between"
      >
        <XStack flex={1} gap={"$space.small"} justifyContent="flex-start">
          {/*<YStack gap={2}>
            {data.indented > 0 && (
              <Button
                icon={ArrowLeft}
                onPress={() => {
                  data.setIndent(data.indented - 1);
                }}
              />
            )}
            {data.indented < 4 && (
              <Button
                icon={ArrowRight}
                onPress={() => {
                  data.setIndent(data.indented + 1);
                }}
              />
            )}
              </YStack>*/}

          <Checkbox
            checked={data.data.checked}
            onCheckedChange={() => data.toggleChecked()}
          />

          <YStack gap={"$space.medium"} flex={1}>
            <CheckboxUIInput data={data} />

            {infoRowItems && <InfoRow items={infoRowItems} />}
          </YStack>
        </XStack>

        <XStack justifyContent="flex-end" gap={"$space.small"}>
          {data.useTags && (
            <Pressable
              onPress={() => {
                setShowTagsModal((v) => !v);
              }}
            >
              <Tags size={"$size.normal"} />
            </Pressable>
          )}

          <Pressable
            onPress={() => {
              setShowUserSheet((v) => !v);
            }}
          >
            <UserPlus size={"$size.normal"} />
          </Pressable>

          <PopoverConfirm
            onConfirm={() => {
              data.deleteData(data.data.label);
            }}
          >
            <Trash2 size={"$size.normal"} color={"$muted"} />
          </PopoverConfirm>
        </XStack>
      </XStack>

      <Sheet
        sheetId="'checkbox_assign_users"
        open={showUserSheet}
        onOpenChange={setShowUserSheet}
        header={<Title>Assign users</Title>}
      >
        <Group borderTop={false}>
          {data.plan.users.map((user) => {
            const isAssigned = data.data.assignedTo.includes(user.uid);
            return (
              <XStack
                style={{ width: "100%" }}
                key={user.uid}
                onPress={() => {
                  data.toggleAssignee(user.uid);
                }}
                cursor="pointer"
                gap={"$medium"}
                alignItems="center"
                flex={1}
              >
                <Checkbox checked={isAssigned} />
                <UserUI uid={user.uid} />
              </XStack>
            );
          })}
        </Group>
      </Sheet>

      <TagsModal
        planUid={data.plan.uid}
        visible={showTagsModal}
        onClose={() => setShowTagsModal(false)}
        selectedTags={data.planTags}
        onSelect={(tag) => {
          data.toggleTag(tag);
        }}
      />
    </>
  );
});
